import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Calling from "../../api/Calling";
import { motion } from "framer-motion";
import {
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Skeleton,
    Tooltip,
} from "@mui/material";
import {
    Settings,
    Link2,
    KeyRound,
    Code2,
    Map as MapIcon,
    Save,
    RefreshCw,
    AlertCircle,
    Phone,
    User,
    MapPin,
    Disc,
    CreditCard,
    Mail,
    Hash,
    ChevronDown,
    ChevronUp,
    CheckCircle2,
    Info,
    Eye,
    EyeOff,
    Copy,
    Check,
} from "lucide-react";

const ApiConfig = () => {
    const [config, setConfig] = useState({
        apiUrl: "",
        apiKey: "",
        responsePath: "data.data",
        mappings: {
            mobile: "mobile",
            alt_number: "alt_number",
            name: "name",
            address: "address",
            circle: "circle",
            fname: "fname",
            id: "id",
            email: "email",
            sub_name: "sub_name",
        },
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showMappings, setShowMappings] = useState(true);
    const [savedSuccess, setSavedSuccess] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [copied, setCopied] = useState(null); // which field was just copied

    // Mapping icons configuration (lucide)
    const mappingIcons = {
        mobile: <Phone size={16} className="text-main-blue" />,
        alt_number: <Phone size={16} className="text-main-blue" />,
        name: <User size={16} className="text-main-blue" />,
        address: <MapPin size={16} className="text-main-blue" />,
        circle: <Disc size={16} className="text-main-blue" />,
        fname: <User size={16} className="text-main-blue" />,
        id: <CreditCard size={16} className="text-main-blue" />,
        email: <Mail size={16} className="text-main-blue" />,
        sub_name: <User size={16} className="text-main-blue" />,
    };

    const mappingDescriptions = {
        mobile: "Mobile number field from API response",
        alt_number: "Alternate mobile number field from API response",
        name: "Name field from API response",
        address: "Address field from API response",
        circle: "Carrier/Circle field from API response",
        fname: "Father's name field from API response",
        id: "Unique ID/UID field from API response",
        email: "Email field from API response",
        sub_name: "Subscriber name field from API response",
    };

    // Light-theme MUI field styling
    const fieldSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "#fff",
            fontSize: "0.875rem",
        },
    };
    const monoFieldSx = {
        ...fieldSx,
        "& .MuiOutlinedInput-input": {
            fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        },
    };

    // Copy helper with transient "copied" feedback
    const handleCopy = async (text, key) => {
        if (!text) {
            toast.info("Nothing to copy");
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    // Fetch Existing Config
    const fetchConfig = async () => {
        setInitialLoading(true);
        setError(null);
        try {
            const resp = await Calling.getApiConfig();
            if (resp?.status) {
                setConfig(resp.data);
            } else {
                setError(resp?.message || "Failed to load API config");
            }
        } catch (err) {
            setError("Something went wrong while fetching config");
        }
        setInitialLoading(false);
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    // Validate form before save
    const validateForm = () => {
        if (!config.apiUrl.trim()) {
            toast.error("API URL is required");
            return false;
        }
        if (!config.responsePath.trim()) {
            toast.error("Response Path is required");
            return false;
        }
        return true;
    };

    // Save / Update Config
    const handleSave = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setSavedSuccess(false);

        try {
            const resp = await Calling.putApiConfig(config);

            if (resp?.status) {
                toast.success(resp.message || "API Configuration saved");
                setSavedSuccess(true);
                setTimeout(() => setSavedSuccess(false), 3000);
            } else {
                toast.error(resp?.message || "Failed to save config");
            }
        } catch (err) {
            toast.error("Something went wrong!");
        }
        setLoading(false);
    };

    // Loading State (MUI shimmer)
    if (initialLoading) {
        return (
            <div className="w-full text-main-font">
                <div className="bg-main-white shadow-md rounded-2xl p-6 w-full border border-main-border">
                    <div className="flex items-center gap-3 mb-6">
                        <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: "12px" }} />
                        <div className="flex-1">
                            <Skeleton variant="text" width={200} height={28} />
                            <Skeleton variant="text" width={280} height={18} />
                        </div>
                    </div>
                    <Skeleton variant="rounded" height={70} sx={{ borderRadius: "12px", mb: 2 }} />
                    {[...Array(3)].map((_, idx) => (
                        <Skeleton
                            key={idx}
                            variant="rounded"
                            height={100}
                            sx={{ borderRadius: "12px", mb: 2 }}
                        />
                    ))}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[...Array(6)].map((_, idx) => (
                            <Skeleton
                                key={idx}
                                variant="rounded"
                                height={90}
                                sx={{ borderRadius: "12px" }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full text-main-font"
        >
            <div className="bg-main-white shadow-md rounded-2xl p-6 w-full border border-main-border">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-main-blue/10 rounded-xl flex items-center justify-center">
                            <Settings className="text-main-blue" size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-main-font">API Configuration</h2>
                            <p className="text-sm text-main-subfont">
                                Configure external API settings and mappings
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                        <Tooltip title="Refresh Config">
                            <span>
                                <button
                                    onClick={fetchConfig}
                                    disabled={loading || initialLoading}
                                    className="p-2.5 bg-main-blue/10 text-main-blue rounded-xl hover:bg-main-blue/20 transition-all disabled:opacity-50"
                                >
                                    <RefreshCw size={18} className={initialLoading ? "animate-spin" : ""} />
                                </button>
                            </span>
                        </Tooltip>
                        <motion.button
                            whileTap={{ scale: 0.96 }}
                            onClick={handleSave}
                            disabled={loading}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all
                                ${loading
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : savedSuccess
                                        ? "bg-green-500 text-white"
                                        : "bg-main-blue text-white hover:bg-main-blue/90"
                                }`}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={16} color="inherit" />
                                    Saving...
                                </>
                            ) : savedSuccess ? (
                                <>
                                    <CheckCircle2 size={18} />
                                    Saved!
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Save Configuration
                                </>
                            )}
                        </motion.button>
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-red-50 border border-red-200">
                        <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                )}

                {/* Info Banner */}
                <div className="flex items-start gap-3 p-4 mb-6 rounded-xl bg-blue-50 border border-blue-200">
                    <Info className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                    <div className="text-sm text-blue-700">
                        <p className="font-medium mb-1">Configuration Guide</p>
                        <p className="text-blue-600">
                            Use{" "}
                            <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">{"{number}"}</code>{" "}
                            as placeholder for mobile number and{" "}
                            <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">{"{apikey}"}</code>{" "}
                            for the API key in the URL.
                        </p>
                    </div>
                </div>

                {/* Configuration Form */}
                <div className="space-y-4">

                    {/* API URL Input */}
                    <div className="bg-main-bg rounded-xl p-4 border border-main-border">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Link2 className="text-main-blue" size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <label className="text-sm font-medium text-main-font flex items-center gap-1">
                                    API URL
                                    <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-main-subfont mb-2">External API endpoint with placeholders</p>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="https://api.example.com?num={number}&key={apikey}"
                                    value={config.apiUrl}
                                    onChange={(e) => setConfig({ ...config, apiUrl: e.target.value })}
                                    sx={monoFieldSx}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Copy URL">
                                                        <IconButton
                                                            onClick={() => handleCopy(config.apiUrl, "apiUrl")}
                                                            size="small"
                                                            edge="end"
                                                        >
                                                            {copied === "apiUrl" ? (
                                                                <Check size={16} className="text-green-500" />
                                                            ) : (
                                                                <Copy size={16} className="text-main-subfont" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* API Key Input */}
                    <div className="bg-main-bg rounded-xl p-4 border border-main-border">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <KeyRound className="text-main-blue" size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <label className="text-sm font-medium text-main-font">API Key</label>
                                <p className="text-xs text-main-subfont mb-2">Authentication key for API requests (optional)</p>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type={showApiKey ? "text" : "password"}
                                    placeholder="Enter your API key"
                                    value={config.apiKey}
                                    onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                                    sx={monoFieldSx}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title={showApiKey ? "Hide key" : "Show key"}>
                                                        <IconButton
                                                            onClick={() => setShowApiKey((s) => !s)}
                                                            size="small"
                                                            aria-label="toggle api key visibility"
                                                        >
                                                            {showApiKey ? (
                                                                <EyeOff size={16} className="text-main-subfont" />
                                                            ) : (
                                                                <Eye size={16} className="text-main-subfont" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Copy API key">
                                                        <IconButton
                                                            onClick={() => handleCopy(config.apiKey, "apiKey")}
                                                            size="small"
                                                            edge="end"
                                                            aria-label="copy api key"
                                                        >
                                                            {copied === "apiKey" ? (
                                                                <Check size={16} className="text-green-500" />
                                                            ) : (
                                                                <Copy size={16} className="text-main-subfont" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Response Path Input */}
                    <div className="bg-main-bg rounded-xl p-4 border border-main-border">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Code2 className="text-main-blue" size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <label className="text-sm font-medium text-main-font flex items-center gap-1">
                                    Response Path
                                    <span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-main-subfont mb-2">Path to data in API response (e.g., data.data, response.result)</p>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="data.data"
                                    value={config.responsePath}
                                    onChange={(e) => setConfig({ ...config, responsePath: e.target.value })}
                                    sx={monoFieldSx}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Response Mappings Section */}
                    <div className="bg-main-bg rounded-xl border border-main-border overflow-hidden">
                        {/* Mappings Header */}
                        <button
                            onClick={() => setShowMappings(!showMappings)}
                            className="w-full flex items-center justify-between p-4 hover:bg-main-th transition-all"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MapIcon className="text-main-blue" size={18} />
                                </div>
                                <div className="text-left">
                                    <span className="text-sm font-medium text-main-font block">Response Mappings</span>
                                    <span className="text-xs text-main-subfont">Map API response fields to database fields</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs bg-main-blue/10 text-main-blue px-2 py-1 rounded-lg font-medium">
                                    {Object.keys(config.mappings).length} fields
                                </span>
                                {showMappings ? (
                                    <ChevronUp className="text-main-subfont" size={18} />
                                ) : (
                                    <ChevronDown className="text-main-subfont" size={18} />
                                )}
                            </div>
                        </button>

                        {/* Mappings Content */}
                        {showMappings && (
                            <div className="border-t border-main-border p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {Object.keys(config.mappings).map((key) => (
                                        <div
                                            key={key}
                                            className="bg-main-white rounded-xl p-3 border border-main-border hover:border-main-blue/30 transition-all"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    {mappingIcons[key] || <Hash size={16} className="text-main-blue" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <label className="text-xs font-medium text-main-font capitalize block mb-0.5">
                                                        {key}
                                                    </label>
                                                    <p className="text-xs text-main-subfont mb-2 truncate" title={mappingDescriptions[key]}>
                                                        {mappingDescriptions[key]}
                                                    </p>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder={`Enter ${key} field name`}
                                                        value={config.mappings[key]}
                                                        onChange={(e) =>
                                                            setConfig({
                                                                ...config,
                                                                mappings: { ...config.mappings, [key]: e.target.value },
                                                            })
                                                        }
                                                        sx={monoFieldSx}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions (Mobile) */}
                <div className="mt-6 pt-6 border-t border-main-border md:hidden">
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={handleSave}
                        disabled={loading}
                        className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium transition-all
                            ${loading
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : savedSuccess
                                    ? "bg-green-500 text-white"
                                    : "bg-main-blue text-white hover:bg-main-blue/90"
                            }`}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={16} color="inherit" />
                                Saving...
                            </>
                        ) : savedSuccess ? (
                            <>
                                <CheckCircle2 size={18} />
                                Saved Successfully!
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                Save Configuration
                            </>
                        )}
                    </motion.button>
                </div>
            </div>
        </motion.div>
    );
};

export default ApiConfig;
